import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Analytics } from "@vercel/analytics/react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SmartRedirect } from "@/components/SmartRedirect";
import { SuspensionGate } from "@/components/SuspensionGate";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { DateFormatProvider } from "@/contexts/DateFormatContext";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ReferralCapture } from "@/components/ReferralCapture";
import { CoachBarProvider } from "@/components/coach/CoachBarProvider";
import { FloatingCoachBar } from "@/components/coach/FloatingCoachBar";
import { CoachPanelRoot } from "@/components/coach/CoachPanelRoot";
import { CoachFAB } from "@/components/coach/CoachFAB";
// Critical path — eagerly loaded (landing, auth, core app shell)
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Index from "./pages/Index";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Lazy-loaded — secondary user pages
const About = lazy(() => import("./pages/About"));
const Settings = lazy(() => import("./pages/Settings"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const Mentorship = lazy(() => import("./pages/Mentorship"));
const Network = lazy(() => import("./pages/Network"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Changelog = lazy(() => import("./pages/Changelog"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));

// Lazy-loaded — admin pages (only accessed by admins)
const AdminOverview = lazy(() => import("./pages/AdminOverview"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));
const AdminSupportTickets = lazy(() => import("./pages/AdminSupportTickets"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));
const AdminEmailPreview = lazy(() => import("./pages/AdminEmailPreview"));
const AdminEmailAnalytics = lazy(() => import("./pages/AdminEmailAnalytics"));
const AdminAutomation = lazy(() => import("./pages/AdminAutomation"));
const AdminValueAssessmentAnalytics = lazy(() => import("./pages/AdminValueAssessmentAnalytics"));
const AdminInviteCodes = lazy(() => import("./pages/AdminInviteCodes"));
const AdminWaitlist = lazy(() => import("./pages/AdminWaitlist"));
const AdminChangelog = lazy(() => import("./pages/AdminChangelog"));
const AdminSocial = lazy(() => import("./pages/AdminSocial"));
const AdminEngagement = lazy(() => import("./pages/AdminEngagement"));
const AdminNpsResponses = lazy(() => import("./pages/AdminNpsResponses"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminEngagementJobs = lazy(() => import("./pages/AdminEngagementJobs"));
const AdminCampaigns = lazy(() => import("./pages/AdminCampaigns"));
const AdminAICosts = lazy(() => import("./pages/AdminAICosts"));

// Lazy-loaded — legal pages
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const DataRetention = lazy(() => import("./pages/DataRetention"));
const DPA = lazy(() => import("./pages/DPA"));
const B2BPrivacy = lazy(() => import("./pages/B2BPrivacy"));
const TeamTerms = lazy(() => import("./pages/TeamTerms"));

// Lazy-loaded — public one-off pages
const MagicLink = lazy(() => import("./pages/MagicLink"));
const NpsFeedback = lazy(() => import("./pages/NpsFeedback"));
const Testimonial = lazy(() => import("./pages/Testimonial"));

const queryClient = new QueryClient();

const RouteLoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// Wrap with CoachPanelRoot for panel + Cmd/K support
const MaybeCoachPanel = CoachPanelRoot;

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Analytics />
      <BrowserRouter>
        <ReferralCapture />
        <DateFormatProvider>
        <ImpersonationProvider>
          <ImpersonationBanner />
          <SuspensionGate>
            <MaybeCoachPanel>
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/welcome" element={<Landing />} />
              <Route path="/about" element={<About />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* Smart redirect determines where to send authenticated users */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <SmartRedirect />
                  </ProtectedRoute>
                }
              />
              {/* Main dashboard with tabs (values, goals, plan, habits, mentorship) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Index />
                  </ProtectedRoute>
                }
              />
              {/* Home dashboard for users who completed values */}
              <Route
                path="/home"
                element={
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/email-preferences"
                element={
                  <ProtectedRoute>
                    <EmailPreferences />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mentorship"
                element={
                  <ProtectedRoute>
                    <Mentorship />
                  </ProtectedRoute>
                }
              />
              {/* Network page — dedicated mentorship UX */}
              <Route
                path="/network"
                element={
                  <ProtectedRoute>
                    <Network />
                  </ProtectedRoute>
                }
              />
              {/* Admin Dashboard */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute>
                    <AdminUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/waitlist"
                element={
                  <ProtectedRoute>
                    <AdminWaitlist />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/invite-codes"
                element={
                  <ProtectedRoute>
                    <AdminInviteCodes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tickets"
                element={
                  <ProtectedRoute>
                    <AdminSupportTickets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/feedback"
                element={
                  <ProtectedRoute>
                    <AdminFeedback />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/email-preview"
                element={
                  <ProtectedRoute>
                    <AdminEmailPreview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/email-analytics"
                element={
                  <ProtectedRoute>
                    <AdminEmailAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ai-costs"
                element={
                  <ProtectedRoute>
                    <AdminAICosts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/changelog"
                element={
                  <ProtectedRoute>
                    <AdminChangelog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/automation"
                element={
                  <ProtectedRoute>
                    <AdminAutomation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/value-assessment-analytics"
                element={
                  <ProtectedRoute>
                    <AdminValueAssessmentAnalytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <ProtectedRoute>
                    <AdminUserDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social/new"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social/calendar"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social/leads"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social/leads/new"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social/leads/:leadId"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/social/:postId"
                element={
                  <ProtectedRoute>
                    <AdminSocial />
                  </ProtectedRoute>
                }
              />
              {/* Engagement Admin */}
              <Route
                path="/admin/engagement"
                element={
                  <ProtectedRoute>
                    <AdminEngagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/nps"
                element={
                  <ProtectedRoute>
                    <AdminNpsResponses />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/testimonials-admin"
                element={
                  <ProtectedRoute>
                    <AdminTestimonials />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/engagement-jobs"
                element={
                  <ProtectedRoute>
                    <AdminEngagementJobs />
                  </ProtectedRoute>
                }
              />
              {/* Campaigns */}
              <Route
                path="/admin/campaigns"
                element={
                  <ProtectedRoute>
                    <AdminCampaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/campaigns/new"
                element={
                  <ProtectedRoute>
                    <AdminCampaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/campaigns/:campaignId"
                element={
                  <ProtectedRoute>
                    <AdminCampaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/campaigns/:campaignId/edit"
                element={
                  <ProtectedRoute>
                    <AdminCampaigns />
                  </ProtectedRoute>
                }
              />
              {/* Changelog (authenticated) */}
              <Route
                path="/changelog"
                element={
                  <ProtectedRoute>
                    <Changelog />
                  </ProtectedRoute>
                }
              />
              {/* Magic link handler for email actions */}
              <Route path="/magic" element={<MagicLink />} />
              <Route path="/nps-feedback" element={<NpsFeedback />} />
              <Route path="/testimonial" element={<Testimonial />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/data-retention" element={<DataRetention />} />
              <Route path="/dpa" element={<DPA />} />
              <Route path="/privacy/b2b" element={<B2BPrivacy />} />
              <Route path="/terms/teams" element={<TeamTerms />} />
              {/* Coach Dashboard */}
              <Route
                path="/coach"
                element={
                  <ProtectedRoute>
                    <CoachDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/coach/:clientId"
                element={
                  <ProtectedRoute>
                    <CoachDashboard />
                  </ProtectedRoute>
                }
              />
              {/* Team Dashboard */}
              <Route
                path="/team/:slug"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/:slug/members"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/:slug/progress"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/:slug/analytics"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/:slug/reports"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/:slug/activity"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team/:slug/settings"
                element={
                  <ProtectedRoute>
                    <TeamDashboard />
                  </ProtectedRoute>
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <CoachBarProvider>
              <FloatingCoachBar />
            </CoachBarProvider>
            <CoachFAB />
            </MaybeCoachPanel>
          </SuspensionGate>
        </ImpersonationProvider>
        </DateFormatProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
